import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';

/**
 * DTO para un item individual en la lista de Control DUS.
 * Representa una instrucción de embarque con su información aduanera completa.
 *
 * @remarks
 * ✅ Estructura validada contra vwControlDusDashboard (24 Enero 2025)
 * - Vista del cliente con 109+ campos disponibles
 * - ClienteId y TotalQuantityOfPallets agregados por el cliente
 * - Campos calculados dinámicamente: estado, Etapa, DiasVencimiento
 *
 * @example
 * ```typescript
 * const item: ControlDusItemDto = {
 *   nroIE: 6213,
 *   clienteId: 376,
 *   cliente: 'MONFRUT',
 *   exportador: 'MONFRUT',
 *   refCliente: '192',
 *   contenedor: 'MSNU8697170',
 *   plts: 0,
 *   dus: '13009996-3',
 *   reserva: '070ISA1205214',
 *   tipoEmbarque: 'Contenedor',
 *   nave: 'MSC SANTA MARIA',
 *   etd: new Date('2025-10-05'),
 *   etapa: 'Legalización',
 *   estado: 'Pendiente',
 *   comentarios: '',
 * };
 * ```
 */
export class ControlDusItemDto {
  // === Identificadores ===

  /**
   * Número de instrucción de embarque (ID único).
   * Corresponde al campo Id de tblShippingInstructions.
   *
   * @example 6213
   */
  @ApiProperty({
    description: 'Número de instrucción de embarque (ID único)',
    example: 6213,
  })
  nroIE!: number;

  /**
   * ID del cliente (numérico).
   * ✅ Campo agregado por el cliente en la vista.
   *
   * @example 376
   */
  @ApiPropertyOptional({
    description: 'ID del cliente',
    example: 376,
  })
  clienteId?: number;

  // === Información Principal ===

  /**
   * Nombre del cliente.
   *
   * @example 'MONFRUT'
   */
  @ApiPropertyOptional({
    description: 'Nombre del cliente',
    example: 'MONFRUT',
  })
  cliente?: string;

  /**
   * Nombre del exportador.
   * Puede diferir del cliente en algunos casos.
   *
   * @example 'MONFRUT'
   */
  @ApiPropertyOptional({
    description: 'Nombre del exportador',
    example: 'MONFRUT',
  })
  exportador?: string;

  /**
   * Referencia del cliente.
   *
   * @example '192'
   */
  @ApiPropertyOptional({
    description: 'Referencia del cliente',
    example: '192',
  })
  refCliente?: string;

  /**
   * Número de contenedor.
   *
   * @example 'MSNU8697170'
   */
  @ApiPropertyOptional({
    description: 'Número de contenedor',
    example: 'MSNU8697170',
  })
  contenedor?: string;

  /**
   * Cantidad de pallets.
   * ✅ Campo agregado por el cliente (TotalQuantityOfPallets).
   *
   * @example 0
   */
  @ApiPropertyOptional({
    description: 'Cantidad de pallets',
    example: 0,
  })
  plts?: number;

  /**
   * Número de DUS.
   *
   * @example '13009996-3'
   */
  @ApiPropertyOptional({
    description: 'Número de DUS',
    example: '13009996-3',
  })
  dus?: string;

  /**
   * SPS Multiport - Código de servicio SPS.
   * Campo numérico que representa el código de servicio SPS.
   *
   * @example 2550
   */
  @ApiPropertyOptional({
    description: 'SPS Multiport - Código de servicio SPS',
    example: 2550,
  })
  sps?: number;

  /**
   * ISF Date - Fecha ISF (Importer Security Filing).
   * Campo de fecha que indica la fecha del ISF.
   *
   * @example '2025-02-22T00:00:00.000Z'
   */
  @ApiPropertyOptional({
    description: 'ISF Date - Fecha ISF (Importer Security Filing)',
    example: '2025-02-22T00:00:00.000Z',
    type: Date,
  })
  isf?: Date;

  /**
   * Número de reserva/booking.
   *
   * @example '070ISA1205214'
   */
  @ApiPropertyOptional({
    description: 'Número de reserva/booking',
    example: '070ISA1205214',
  })
  reserva?: string;

  // === Información de Embarque ===

  /**
   * Tipo de embarque.
   * Valores posibles: 'Contenedor', 'Pallet'
   *
   * @example 'Contenedor'
   */
  @ApiPropertyOptional({
    description: 'Tipo de embarque',
    example: 'Contenedor',
    enum: ['Contenedor', 'Pallet'],
  })
  tipoEmbarque?: string;

  /**
   * Nombre de la nave.
   *
   * @example 'MSC SANTA MARIA'
   */
  @ApiPropertyOptional({
    description: 'Nombre de la nave',
    example: 'MSC SANTA MARIA',
  })
  nave?: string;

  /**
   * Número de viaje.
   *
   * @example '540'
   */
  @ApiPropertyOptional({
    description: 'Número de viaje',
    example: '540',
  })
  viaje?: string;

  /**
   * Puerto de carga (Port of Loading).
   *
   * @example 'VALPARAISO'
   */
  @ApiPropertyOptional({
    description: 'Puerto de carga (Port of Loading)',
    example: 'VALPARAISO',
  })
  pol?: string;

  /**
   * Puerto de descarga (Port of Discharge).
   *
   * @example 'HELSINGBORG'
   */
  @ApiPropertyOptional({
    description: 'Puerto de descarga (Port of Discharge)',
    example: 'HELSINGBORG',
  })
  pod?: string;

  /**
   * Fecha ETD (Estimated Time of Departure).
   *
   * @example '2025-10-05T00:00:00.000Z'
   */
  @ApiPropertyOptional({
    description: 'Fecha ETD (Estimated Time of Departure)',
    example: '2025-10-05T00:00:00.000Z',
    type: Date,
  })
  etd?: Date;

  /**
   * Fecha ETA (Estimated Time of Arrival).
   *
   * @example '2025-10-26T00:00:00.000Z'
   */
  @ApiPropertyOptional({
    description: 'Fecha ETA (Estimated Time of Arrival)',
    example: '2025-10-26T00:00:00.000Z',
    type: Date,
  })
  eta?: Date;

  /**
   * Días de tránsito.
   *
   * @example 19
   */
  @ApiPropertyOptional({
    description: 'Días de tránsito',
    example: 19,
  })
  dias?: number;

  // === Estado y Control ===

  /**
   * Etapa del proceso.
   * Valores posibles: 'Documentación', 'Legalización', 'Inicial'
   * Campo calculado dinámicamente en la vista.
   *
   * @example 'Legalización'
   */
  @ApiPropertyOptional({
    description: 'Etapa del proceso',
    example: 'Legalización',
    enum: ['Documentación', 'Legalización', 'Inicial'],
  })
  etapa?: string;

  /**
   * Estado actual.
   * Valores posibles: 'Pendiente', 'En Proceso', 'Finalizado'
   * Campo calculado dinámicamente en la vista.
   * ⚠️ IMPORTANTE: El campo en la vista está en minúscula (estado, no Estado)
   *
   * @example 'Pendiente'
   */
  @ApiProperty({
    description: 'Estado actual',
    example: 'Pendiente',
    enum: ['Pendiente', 'En Proceso', 'Finalizado'],
  })
  estado!: string;

  /**
   * Número de despacho.
   *
   * @example '18579'
   */
  @ApiPropertyOptional({
    description: 'Número de despacho',
    example: '18579',
  })
  despacho?: string;

  /**
   * Estado en aduana.
   *
   * @example 'Con despacho y dus'
   */
  @ApiPropertyOptional({
    description: 'Estado en aduana',
    example: 'Con despacho y dus',
  })
  estadoAduana?: string;

  // === Fechas y Vencimientos ===

  /**
   * Fecha de aceptación del DUS.
   *
   * @example '2025-09-29'
   */
  @ApiPropertyOptional({
    description: 'Fecha de aceptación del DUS',
    example: '2025-09-29',
    type: String,
  })
  fechaAceptacion?: string;

  /**
   * Fecha de legalización del DUS (desde tblDus.LegalizationDate).
   *
   * @example '2025-10-15'
   */
  @ApiPropertyOptional({
    description: 'Fecha de legalización del DUS',
    example: '2025-10-15',
    type: String,
  })
  fechaLegalizacion?: string;

  /**
   * Indica si tiene prórroga.
   * Valores posibles: 'SI', 'NO'
   *
   * @example 'SI'
   */
  @ApiPropertyOptional({
    description: 'Indica si tiene prórroga',
    example: 'SI',
    enum: ['SI', 'NO'],
  })
  prorroga?: string;

  /**
   * Fecha de vencimiento.
   *
   * @example '2025-11-18'
   */
  @ApiPropertyOptional({
    description: 'Fecha de vencimiento',
    example: '2025-11-18',
    type: String,
  })
  vencimiento?: string;

  /**
   * Días hasta vencimiento.
   * Valor negativo indica vencido.
   *
   * @example 25
   */
  @ApiPropertyOptional({
    description: 'Días hasta vencimiento',
    example: 25,
  })
  diasVencimiento?: number;

  /**
   * Rango de vencimiento.
   * Valores posibles: 'POR VENCER', 'VIGENTE', 'VENCIDO'
   *
   * @example 'POR VENCER'
   */
  @ApiPropertyOptional({
    description: 'Rango de vencimiento',
    example: 'POR VENCER',
    enum: ['POR VENCER', 'VIGENTE', 'VENCIDO'],
  })
  rangoVencimiento?: string;

  // === Información Adicional ===

  /**
   * Tipo de atraso.
   *
   * @example 'SIN INFORMACION'
   */
  @ApiPropertyOptional({
    description: 'Tipo de atraso',
    example: 'SIN INFORMACION',
  })
  tipoAtraso?: string;

  /**
   * Tipo de atraso para gráficos.
   * Valores abreviados para visualización.
   *
   * @example 'NO INGRESADO'
   */
  @ApiPropertyOptional({
    description: 'Tipo de atraso para gráficos',
    example: 'NO INGRESADO',
  })
  tipoAtrasoGrafico?: string;

  /**
   * Comentarios y observaciones.
   * Mapeado desde el campo Remarks de la vista.
   *
   * @example ''
   */
  @ApiProperty({
    description: 'Comentarios y observaciones',
    example: '',
  })
  comentarios!: string;

  /**
   * Ejecutivo responsable.
   *
   * @example 'Jorge Alvarado'
   */
  @ApiPropertyOptional({
    description: 'Ejecutivo responsable',
    example: 'Jorge Alvarado',
  })
  ejecutivo?: string;

  // === Partes Adicionales (disponibles pero opcionales) ===

  /**
   * Nombre del consignatario.
   *
   * @example 'CONSIGNATARIO S.A.'
   */
  @ApiPropertyOptional({
    description: 'Nombre del consignatario',
    example: 'CONSIGNATARIO S.A.',
  })
  consigneeShortName?: string;

  /**
   * Nombre del notify.
   *
   * @example 'NOTIFY S.A.'
   */
  @ApiPropertyOptional({
    description: 'Nombre del notify',
    example: 'NOTIFY S.A.',
  })
  notifyShortName?: string;

  /**
   * Nombre del agente aduanal.
   *
   * @example 'AGENTE ADUANAL'
   */
  @ApiPropertyOptional({
    description: 'Nombre del agente aduanal',
    example: 'AGENTE ADUANAL',
  })
  customsAgentName?: string;

  /**
   * Nombre de la naviera.
   *
   * @example 'MSC'
   */
  @ApiPropertyOptional({
    description: 'Nombre de la naviera',
    example: 'MSC',
  })
  carrierName?: string;

  // === Información Comercial (disponible pero opcional) ===

  /**
   * Tipo de carga.
   *
   * @example 'Refrigerada'
   */
  @ApiPropertyOptional({
    description: 'Tipo de carga',
    example: 'Refrigerada',
  })
  typeOfCargoName?: string;

  /**
   * Modo de transporte.
   *
   * @example 'Marítimo'
   */
  @ApiPropertyOptional({
    description: 'Modo de transporte',
    example: 'Marítimo',
  })
  wayOfTransportName?: string;

  /**
   * Incoterm.
   *
   * @example 'FOB'
   */
  @ApiPropertyOptional({
    description: 'Incoterm',
    example: 'FOB',
  })
  clauseOfSaleName?: string;
}
